import { lazy, Suspense, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { ErrorBoundary, RouteErrorBoundary } from "@/components/ErrorBoundary";
import { usePageTracking } from "@/hooks/usePageTracking";
import { LiquidGlassFilter } from "@/components/LiquidGlassFilter";

// Eagerly load the landing page for fast initial render
import Landing from "./pages/Landing";
import rubbyLogo from "@/assets/rubby-logo.png";

// Lazy load all other routes to reduce initial bundle size
const CreateWizard = lazy(() => import("./pages/CreateWizard"));
const StorybookWizard = lazy(() => import("./pages/StorybookWizard"));
const PhotoToGame = lazy(() => import("./pages/PhotoToGame"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const Auth = lazy(() => import("./pages/Auth"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const QualitySafety = lazy(() => import("./pages/QualitySafety"));
const Gallery = lazy(() => import("./pages/Gallery"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Account = lazy(() => import("./pages/Account"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Home = lazy(() => import("./pages/Home"));
const AnimationWizard = lazy(() => import("./pages/AnimationWizard"));
const Game = lazy(() => import("./pages/Game"));
const SharedCreation = lazy(() => import("./pages/SharedCreation"));
const BuyCredits = lazy(() => import("./pages/BuyCredits"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));

const queryClient = new QueryClient();

const SuspenseProgressBar = () => {
  const [width, setWidth] = useState('0%');

  useEffect(() => {
    const t = requestAnimationFrame(() => setWidth('80%'));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <div className="fixed top-14 left-0 right-0 z-50 h-0.5 bg-primary/20">
      <div
        className="h-full bg-primary transition-all duration-[1500ms] ease-out"
        style={{ width }}
      />
    </div>
  );
};

const PageLoaderMessage = () => {
  const { t } = useLanguage();
  return (
    <p className="text-muted-foreground text-sm font-handwritten animate-fade-in-delayed">
      {t('common.loadingMagic')}
    </p>
  );
};

const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-4">
    <img
      src={rubbyLogo}
      alt="Rubby"
      className="w-10 h-10 object-contain animate-pulse"
    />
    <PageLoaderMessage />
  </div>
);

const AppRoutes = () => {
  usePageTracking();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/home" element={<Home />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/about" element={<About />} />
      <Route path="/quality-safety" element={<QualitySafety />} />
      <Route
        path="/toy"
        element={
          <>
            <div
              aria-hidden
              className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage:
                  "url(https://hdtgqbtxmtvridkxsjcd.supabase.co/storage/v1/object/public/demo/Website/R_BG.avif)",
              }}
            />
            <CreateWizard />
          </>
        }
      />
      <Route
        path="/story"
        element={
          <>
            <div
              aria-hidden
              className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage:
                  "url(https://hdtgqbtxmtvridkxsjcd.supabase.co/storage/v1/object/public/demo/Website/U_BG.avif)",
              }}
            />
            <StorybookWizard />
          </>
        }
      />
      <Route
        path="/animation"
        element={
          <>
            <div
              aria-hidden
              className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage:
                  "url(https://hdtgqbtxmtvridkxsjcd.supabase.co/storage/v1/object/public/demo/Website/B_BG.avif)",
              }}
            />
            <AnimationWizard />
          </>
        }
      />
      <Route path="/game" element={<Game />} />
      <Route path="/photo-to-game" element={<PhotoToGame />} />
      <Route path="/track" element={<OrderTracking />} />
      <Route path="/order/:orderId" element={<OrderTracking />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/reviews" element={<Reviews />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/account" element={<Account />} />
      <Route path="/credits" element={<BuyCredits />} />
      <Route path="/payments/callback" element={<PaymentCallback />} />
      <Route path="/share/:projectId" element={<SharedCreation />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <LiquidGlassFilter />
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <Layout>
                <RouteErrorBoundary>
                  <Suspense fallback={<><SuspenseProgressBar /><PageLoader /></>}>
                    <AppRoutes />
                  </Suspense>
                </RouteErrorBoundary>
              </Layout>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
